import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Checkbox,
  CircularProgress,
  Stack,
  Tooltip,
} from "@mui/material";

import Grid from "@mui/material/Grid2";
import { Download as DownloadIcon } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import * as XLSX from "xlsx";
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import GeneralContext from "../GeneralContext/GeneralContext";
import InputFilter from "../GeneralComponents/InputFilter";
import PageButtonSimple from "../GeneralComponents/PageButtonSimple";
import PageButtonWhite from "../GeneralComponents/PageButtonWhite";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import ListEmpty from "../GeneralComponents/ListEmpty";
import axios from "../../api/axios";
import InputChipSelect from "../GeneralComponents/InputChipSelect";

import {
  ROL_ADMIN,
  ROL_ADMIN_SUBTEL,
  ROL_VAL_SUBTEL,
  ROL_MINEDUC_ADMIN,
} from "../../Constants/Constants";

const jornadas = {
  a: "8:00 a 20:00 (A)",
  b: "8:00 a 16:00 (B)",
  c: "8:00 a 18:00 (C)",
  d: "8:00 a 23:30 (D)",
  e: "Ninguno (E)",
};

const allColumns = [
  { id: "checkbox", label: "", minWidth: 20, align: "left" },
  { id: "rbd", label: "RBD", minWidth: 70, align: "center" },
  { id: "Zona", label: "Zona", minWidth: 100 },
  { id: "Nombre", label: "Nombre", minWidth: 100, minWidth: 100 },
  { id: "nombre_ee", label: "Nombre Establecimiento", minWidth: 100 },
  { id: "convocatoria", label: "Convocatoria", minWidth: 100 },
  { id: "operador", label: "Operador", minWidth: 100 },
  { id: "nombre_operador", label: "Operador", minWidth: 100 },
  { id: "Fecha_de_Alta", label: "Fecha de Alta", minWidth: 80 },
  { id: "Fecha_Baja", label: "Fecha de Baja", minWidth: 90 },
  { id: "Fecha_recepcion", label: "Recepción de obras", minWidth: 90 },
  { id: "Fecha_servicio", label: "Inicio de servicio", minWidth: 90 },
  { id: "estado", label: "Estado", minWidth: 70 },
  { id: "jornada", label: "Ventana Horaria", minWidth: 70 },
  { id: "representante", label: "Representante", minWidth: 70 },
  { id: "estado_pago", label: "Estado Pago", minWidth: 80 },
  { id: "concurso", label: "Convocatoria", minWidth: 70 },
  { id: "detalle", label: "Ficha RBD", minWidth: 80 },
];

const arrayFields = ["rbd"];

function buildJornada(row) {
  const arr = Array.isArray(row?.jornadas) ? row.jornadas : [];
  const keys = arr
    .map((j) => String(j ?? "").trim().toLowerCase())
    .filter(Boolean);

  if (!keys.length) return { text: jornadas.e, keys: ["e"] };

  const uniqueKeys = Array.from(new Set(keys));
  const text = uniqueKeys.map((k) => jornadas[k] ?? k).join(" - ");
  return { text: text || jornadas.e, keys: uniqueKeys.length ? uniqueKeys : ["e"] };
}

function buildRepresentantes(row) {
  const rrpp = Array.isArray(row?.rrpp) ? row.rrpp : [];
  return rrpp
    .map((p) => `${String(p?.nombre ?? "").trim()} ${String(p?.apellidos ?? "").trim()}`.trim())
    .filter(Boolean);
}

function labelToJornadaKey(label) {
  const s = String(label ?? "").trim();
  const found = Object.entries(jornadas).find(([, v]) => String(v) === s);
  return found ? found[0] : null;
}

export default function RbdGenericList({
  columnOrder = [],
  filtersConfig = [],
  pageTitleOverride = "Listado estado de parque",
  opview = false,
}) {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [tableData, setTableData] = useState([]);
  const [filteredColumns, setFilteredColumns] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const { setPageTitle, includeRol, userSesion } = useContext(GeneralContext);
  const [loadingPDF, setLoadingPDF] = useState(false);

  const jornadaOptions = useMemo(() => {
    const keySet = new Set();
    rows.forEach((r) => {
      (Array.isArray(r?.jornada_keys) ? r.jornada_keys : []).forEach((k) =>
        keySet.add(String(k).toLowerCase())
      );
    });

    return Array.from(keySet)
      .sort((a, b) => a.localeCompare(b, "es", { numeric: true }))
      .map((k) => ({ jornada: jornadas[k] ?? k }));
  }, [rows]);

  const representanteOptions = useMemo(() => {
    const reps = new Set();
    rows.forEach((r) => {
      (Array.isArray(r?.representante) ? r.representante : []).forEach((name) => {
        const n = String(name ?? "").trim();
        if (n) reps.add(n);
      });
    });

    return Array.from(reps)
      .sort((a, b) => a.localeCompare(b, "es", { numeric: true }))
      .map((name) => ({ representante: name }));
  }, [rows]);

  const mapParque = (data) =>
    data.map((row, index) => {
      const estadoBase = opview
        ? row["estado_conectividad"]
        : row["Estado"];

      const j = buildJornada(row);
      const reps = buildRepresentantes(row);

      return {
        ...row,
        estado_pago:
          row["estado_pago"] === "Facturar"
            ? "Facturando"
            : row["estado_pago"] === "Devengar"
            ? "Devengando"
            : "No Facturando",

        estado:
          estadoBase === "Activo" || estadoBase === "Facturar"
            ? "Conectado"
            : estadoBase === "NYA"
            ? "En proceso"
            : "Baja",

        jornada: j.text,
        jornada_keys: j.keys,

        representante: reps,

        operador: row["Operador"] || "Desconocido",

        Fecha_de_Alta: row["Fecha_de_Alta"]?.split("T")[0] || "",
        Fecha_Baja: row["Fecha_de_Baja"]?.split("T")[0] || null,
        Fecha_servicio: row["Fecha_de_Alta"]?.split("T")[0] || null,
        Fecha_recepcion: row["fecha_recepcion_obra"]?.split("T")[0] || null,

        Fecha_Baja_link: row["oficio_baja_url"] || "",
        Fecha_servicio_link: row["oficio_inicio_servicio_url"] || "",
        Fecha_recepcion_link: row["oficio_recepcion_de_obra_url"] || "",

        Fecha_Baja_periodo: row["Fecha_de_Baja"]?.slice(0, 7) || null,
        Fecha_servicio_periodo: row["Fecha_de_Alta"]?.slice(0, 7) || null,
        Fecha_recepcion_periodo: row["fecha_recepcion_obra"]?.slice(0, 7) || null,

        index,
        id: index,
      };
    });

  const fetchData = async () => {
    setLoadingTable(true);
    try {
      let parqueResp;

      if (includeRol([ROL_ADMIN, ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL, ROL_MINEDUC_ADMIN]) && !opview) {
        parqueResp = await axios.getParque();
      } else {
        if (includeRol([ROL_ADMIN, ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL, ROL_MINEDUC_ADMIN])){
          parqueResp = await axios.getParqueDetalle(null);
        } else {
          const opResp = await axios.getOperator(userSesion.id);
          const id_operador = opResp?.data?.id_operador;
          parqueResp = id_operador != null ? await axios.getParqueDetalle(id_operador) : { data: [] };
        }
        
      }

      const modified = mapParque(parqueResp.data || []);
      setRows(modified);
      setTableData(modified);
    } catch (e) {
      console.error("Error cargando parque:", e);
      setRows([]);
      setTableData([]);
    } finally {
      setLoadingTable(false);
    }

    setFilteredColumns(
      columnOrder.length > 0
        ? columnOrder.map((id) => allColumns.find((c) => c.id === id)).filter(Boolean)
        : allColumns
    );
  };

  const downloadPDF = (url, rbd) => {
    setLoadingPDF(true);
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/pdf",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = rbd + ".pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
        setLoadingPDF(false);
      })
      .catch((error) => {
        setLoadingPDF(false);
        console.error("Error al descargar el archivo:", error);
        window.open(url, "_blank");
      });
  };

  const filterRecords = (name, value) => {
    const filterObj = { ...filters, [name]: value };

    const isEmpty = value === null || (Array.isArray(value) && value.length === 0);
    if (isEmpty) delete filterObj[name];

    const newTableData =
      Object.keys(filterObj).length !== 0
        ? rows.filter((item) =>
            Object.entries(filterObj).every(([key, val]) => {
              if (key === "jornada") {
                const wantedKey = labelToJornadaKey(val);
                if (!wantedKey) return false;
                const ks = Array.isArray(item?.jornada_keys) ? item.jornada_keys : [];
                return ks.map((x) => String(x).toLowerCase()).includes(String(wantedKey).toLowerCase());
              }

              if (key === "representante") {
                const wanted = String(val ?? "").trim();
                const reps = Array.isArray(item?.representante) ? item.representante : [];
                return reps.includes(wanted);
              }

              const itemValue = item[key];

              if (arrayFields.includes(key) && Array.isArray(val)) {
                return val.includes(itemValue);
              }

              return itemValue === val;
            })
          )
        : rows;

    if (JSON.stringify(newTableData) !== JSON.stringify(tableData)) {
      setSelected([]);
    }

    const maxPage = Math.floor((newTableData.length - 1) / rowsPerPage);
    if (page > maxPage) setPage(0);

    setFilters(filterObj);
    setTableData(newTableData);
  };

  const downloadXLS = (useFiltered = true) => {
    const sourceData = useFiltered
      ? tableData.filter((row) => selected.includes(row.index))
      : tableData;

    const exportCols = filteredColumns.filter((c) => c && c.id !== "checkbox" && c.id !== "detalle");
    const headers = exportCols.map((c) => c.label || c.id);

    const rowsAOA = sourceData.map((row) =>
      exportCols.map((c) => {
        const v = row[c.id];
        if (c.id === "representante") return (Array.isArray(v) ? v : []).join(" - ");
        return v === undefined || v === null ? "" : v;
      })
    );

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rowsAOA]);
    XLSX.utils.book_append_sheet(wb, ws, "Tabla");
    XLSX.writeFile(wb, "tabla.xlsx");
  };

  const handleSelectAll = (event) => {
    setSelected(event.target.checked ? tableData.map((row) => row.index) : []);
  };

  const handleCheckboxClick = (index) => {
    setSelected((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  const isSelected = (index) => selected.includes(index);

  useEffect(() => {
    setPageTitle(pageTitleOverride);
    fetchData();
  }, []);

  return (
    <>
      <Grid container spacing={1} sx={{ mb: 0, marginTop: "-2rem" }} alignItems="flex-start">
        <Grid size={6}>
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {filtersConfig.map(({ item, name, size = 6 }) => (
              <Grid key={item} size={size}>
                <InputFilter
                  data={
                    item === "jornada"
                      ? jornadaOptions
                      : item === "representante"
                      ? representanteOptions
                      : rows
                  }
                  item={item}
                  name={name}
                  callback={filterRecords}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid container size={6} spacing={0}>
          <Grid size={12}>
            <InputChipSelect data={rows} item={"rbd"} name={"RBD"} callback={filterRecords} />
          </Grid>
        </Grid>
      </Grid>

      {loadingTable ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ mb: 2, p: 1, textAlign: "center" }}>
            <Typography variant="h1" color="inherit" noWrap sx={{ textAlign: "center", padding: "0.5rem" }}>
              {pageTitleOverride}
            </Typography>
          </Paper>

          <PageBgBlock pad="0">
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {filteredColumns.map((col) => (
                      <TableCell key={col.id} align={col.align || "left"}>
                        {col.id === "checkbox" ? (
                          <Checkbox
                            indeterminate={selected.length > 0 && selected.length < tableData.length}
                            checked={tableData.length > 0 && selected.length === tableData.length}
                            onChange={handleSelectAll}
                          />
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {tableData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow key={row.index} hover selected={isSelected(row.index)}>
                        {filteredColumns.map((col) => (
                          <TableCell key={col.id} align={col.align || "left"}>
                            {col.id === "checkbox" ? (
                              <Checkbox checked={isSelected(row.index)} onClick={() => handleCheckboxClick(row.index)} />
                            ) : col.id === "detalle" ? (
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open("/establecimiento_detalle/" + row["rbd"], "_blank");
                                }}
                                key={`${col.id}.button.${index}`}
                                variant="text"
                              >
                                <Typography
                                  key={`${col.id}.typ.${index}`}
                                  sx={{ color: "#111", textTransform: "capitalize", textDecoration: "underline" }}
                                >
                                  Ver
                                </Typography>
                              </Button>
                            ) : col.id.startsWith("Fecha_") && row[col.id] ? (
                              <PageButtonSimple
                                disabled={!row[col.id + "_link"]}
                                width="105px"
                                pad="0.2rem 0.5rem"
                                fs="0.6875rem"
                                startIcon={<DownloadIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadPDF(row[col.id + "_link"], row[col.rbd]);
                                }}
                              >
                                <Stack>
                                  <span>{row[col.id]}</span>
                                </Stack>
                              </PageButtonSimple>
                            ) : col.id.startsWith("Fecha_") ? (
                              <Stack>
                                <span>
                                  <b>{"-"}</b>
                                </span>
                              </Stack>
                            ) : col.id === "estado" ? (
                              <div style={{ display: "flex", alignItems: "center", flexWrap: "no-wrap" }}>
                                <FiberManualRecordIcon
                                  sx={{
                                    fontSize: "0.75rem",
                                    color:
                                      row[col.id] === "Conectado"
                                        ? "#41AE43"
                                        : row[col.id] === "En proceso"
                                        ? "#ABABB5"
                                        : "#FB3B3B",
                                    marginRight: "0.5rem",
                                  }}
                                />
                                {row[col.id]}
                              </div>
                            ) : col.id === "representante" ? (
                              (() => {
                                const reps = Array.isArray(row[col.id]) ? row[col.id] : [];
                                const first = reps[0] ?? "-";
                                const rest = reps.slice(1);

                                return (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        width: 150,
                                        minWidth: 0,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          minWidth: 0,
                                        }}
                                        title={first}
                                      >
                                        {first}
                                      </Typography>

                                      {rest.length > 0 && (
                                        <Tooltip
                                          arrow
                                          placement="top"
                                          title={
                                            <Box sx={{ py: 0.5 }}>
                                              {rest.map((n, i) => (
                                                <Typography
                                                  key={`${row.index}.rep.${i}`}
                                                  variant="body2"
                                                  sx={{ color: "white" }}
                                                >
                                                  {n}
                                                </Typography>
                                              ))}
                                            </Box>
                                          }
                                          componentsProps={{
                                            tooltip: {
                                              sx: {
                                                backgroundColor: "#003B8D",
                                                fontSize: "0.75rem",
                                              },
                                            },
                                            arrow: {
                                              sx: {
                                                color: "#003B8D",
                                              },
                                            },
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              cursor: "pointer",
                                            }}
                                          >
                                            <PersonAddAltOutlinedIcon sx={{ fontSize: 24}}/>
                                          </Box>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  );
                              })()
                            ) : (
                              row[col.id]
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>

              {tableData.length === 0 && <ListEmpty />}

              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 25, 100]}
                count={tableData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, p) => setPage(p)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </TableContainer>
          </PageBgBlock>

          <Grid container spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
            <Grid item size={{ xs: 12, sm: 6, md: 5 }}>
              <PageButtonWhite onClick={() => downloadXLS(false)} width="100%" disabled={tableData.length === 0}>
                Descargar el total de RBD ({tableData.length}) como tabla (.xls)
              </PageButtonWhite>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
              <PageButtonWhite onClick={() => downloadXLS(true)} width="100%" disabled={selected.length === 0}>
                Descargar selección como tabla (.xls)
              </PageButtonWhite>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
